import dayjs from 'dayjs';
import { getDefaultLanguage } from './browser';
import { USER_LANGUAGES } from '../assets/AppConstants';

export const addDate = (date, amount, dateType) => {
  if (dateType === 'days') {
    date.setDate(date.getDate() + amount);
  }

  if (dateType === 'weeks') {
    date.setDate(date.getDate() + (7 * amount));
  }

  if (dateType === 'months') {
    date.setMonth(date.getMonth() + amount);
  }

  if (dateType === 'years') {
    date.setFullYear(date.getFullYear() + amount);
  }

  return date;
};

const getLangToDate = () => {
  let language = getDefaultLanguage().id;
  const userString = localStorage.getItem('user');
  if (userString && userString !== 'null') {
    const useUser = JSON.parse(userString);
    if (useUser?.language) language = useUser.language;
  }

  return USER_LANGUAGES.find((el) => el.id.toLocaleLowerCase() === language.toLocaleLowerCase());
};

/**
 * Locale do usuário como o dayjs o nomeia (`pt-br`, não `pt`). O antecessor derivava isso de
 * `name.toLowerCase()`, que dava `pt` — o `dayjsLocale` existe em `USER_LANGUAGES` para isso.
 */
const userLocale = () => getLangToDate()?.dayjsLocale ?? 'en';

/** dayjs já preso ao locale do usuário. Os formatos longos (`L`, `ll`, `lll`, `LTS`) e o
 * `calendar.sameElse` vivem em `src/config/dayjs.ts`, não são redefinidos a cada chamada. */
const localized = (value?) => dayjs(value).locale(userLocale());

export const formatDate = (date, format = 'L LTS') => localized(date).format(format);

export const isTodayOrFuture = (dateFrom) => dayjs(dayjs().format('YYYY-MM-DD')).diff(dayjs(dateFrom).format('YYYY-MM-DD')) <= 0;

export const getDate = (date, format = 'L') => localized(date).format(format);

export const todayIsBefore = (date) => {
  const todayDate = dayjs(dayjs().format('YYYY-MM-DD'));
  const pastDate = dayjs(date, 'YYYY-MM-DD');

  return todayDate.isBefore(pastDate);
};

export const toTimeString = (date) => dayjs(new Date(date)).format('HH:mm ');

export const toDateTimeString = (date) => dayjs(new Date(date)).format('YYYY-MM-DD HH:mm');

export const getNow = (config: Record<string, any> = {}) => {
  const { format = 'YYYY-MM-DD' } = config;
  return localized(undefined).format(format);
};

export const getDayName = (date) => localized(date).format('dddd');

export const getDayNumber = (date) => localized(date).format('DD');

export const getWeekday = (date) => localized(date).day();

export const timesList = () => {
  const start = dayjs().startOf('day');
  const times = 24 * 4; // 24 hours * 15 mins in an hour
  const items = [];

  for (let i = 0; i < times; i += 1) {
    const time = start.add(15 * i, 'minute');
    const value = time.format('HH:mm:ss');
    const name = time.format('HH:mm ');

    items.push({ name, value });
  }

  return items;
};

export const dateToDB = (date) => dayjs(date, 'YYYY-MM-DD HH:mm').toISOString();

/** Fábrica já com o locale do usuário, para os call sites que precisam de `.fromNow()` e
 * `.calendar()` sobre um valor qualquer. Era `getMoment`. */
export const getDayjs = () => localized;

export const enumerateDaysBetweenDates = (startDate, endDate) => {
  const dates = [];

  const lastDate = dayjs(endDate).startOf('day');
  let currDate = dayjs(startDate).startOf('day');

  dates.push({
    date: dayjs(startDate),
    dateType: 'DAILY',
  });

  // o dia avança antes da comparação, como no `while (curr.add(1,'days').diff(last) < 0)` original
  currDate = currDate.add(1, 'day');
  while (currDate.diff(lastDate) < 0) {
    dates.push({
      date: currDate,
      dateType: 'DAILY',
    });
    currDate = currDate.add(1, 'day');
  }

  if (startDate !== endDate) {
    dates.push({
      date: dayjs(endDate),
      dateType: 'DAILY',
    });
  }

  return dates;
};

export const enumerateMonthsBetweenDates = (date1, date2) => {
  const startDate = dayjs(date1);
  const endDate = dayjs(date2);
  const betweenMonths = [];

  if (startDate.valueOf() <= endDate.valueOf()) {
    const lastMoment = endDate.endOf('month');
    let date = startDate.startOf('month');

    while (date.valueOf() < lastMoment.valueOf()) {
      betweenMonths.push({
        date,
        dateType: 'MONTHLY',
      });

      if (date.format('MM') === '12') {
        betweenMonths.push({
          date,
          dateType: 'ANNUAL',
        });
      }

      date = date.add(1, 'month');
    }
  }

  return betweenMonths;
};

export const millisToMinutesAndSeconds = (millis) => {
  const minutes = Math.floor(millis / 60000);
  const seconds = ((millis % 60000) / 1000).toFixed(0);
  return `${minutes}:${Number(seconds) < 10 ? '0' : ''}${seconds}`;
};

export const listFormat = (list, options: Intl.ListFormatOptions = { style: 'long', type: 'conjunction' }) => {
  const lang = getLangToDate()?.name?.toLowerCase();
  const formatter = new Intl.ListFormat(lang, options);

  return formatter.format(list);
};

export const getWeekRanges = (month) => {
  const ranges = [];
  const endDate = dayjs(month, 'YYYY-MM').endOf('month');

  let startDate = dayjs(month, 'YYYY-MM').startOf('month');
  if (startDate.day() !== 1) {
    startDate = startDate.add(1, 'week').day(1);
  }

  while (startDate.isBefore(endDate)) {
    const weekStart = startDate;
    let weekEnd = weekStart.day(5);

    if (weekEnd.isAfter(endDate)) {
      weekEnd = endDate;
    }

    ranges.push(`${weekStart.format('YYYY-MM-DD')} - ${weekEnd.format('YYYY-MM-DD')}`);

    startDate = startDate.add(1, 'week');
  }

  return ranges;
};

export const getCountDays = (dateFrom, dateTo) => dayjs(dateTo).diff(dayjs(dateFrom), 'day');

export const isWeekend = (date) => [0, 6].includes(dayjs(date).day());

export const isHoliday = (date, holidays = []) => !!holidays.find((el) => el === date);

export const getCountWorkingDays = (dateFrom, dateTo, holidays) => {
  let days = 0;

  let currDate = dayjs(dateFrom).add(1, 'day');
  const lastDate = dayjs(dateTo).add(1, 'day');

  while (currDate.diff(lastDate) < 0) {
    const formatedDate = currDate.format('YYYY-MM-DD');
    if (!isHoliday(formatedDate, holidays) && !isWeekend(formatedDate)) {
      days += 1;
    }

    currDate = currDate.add(1, 'day');
  }

  return days;
};
